import type { JSX, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Formik } from 'formik';
import styled from 'styled-components';

import { setErrorFields } from '../../store/fieldError';
import type { ApiServiceLikeHelper } from '../../utils/formHelpers';
import { handleSubmit } from '../../utils/formHelpers';
import { getFormTemplateFromCustomer } from '../../utils/routeFormatter';
import FormButtonNavigation from '../FormButtonNavigation';
import Notification from '../Notification';

const Wrapper = styled.div`
  width: 100%;
`;

const isEmptyObject = (obj?: Record<string, unknown>) => !obj || Object.keys(obj).length === 0;

const checkDisableSubmitFunc = (disableProgress: boolean, hasInvalidFin: boolean, policyAccepted: boolean) =>
  disableProgress || hasInvalidFin || !policyAccepted;

const checkHasInvalidFin = (
  regionWithNoAttachmentsValidation: string[],
  region: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formPages: any[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attachments: any
) =>
  regionWithNoAttachmentsValidation.includes(region.toLowerCase())
    ? false
    : formPages.find(pageProps => pageProps.validateFinancials) && attachments.locked;

/* ----------------------------------
   Types
---------------------------------- */

interface PageProps {
  children: ReactNode;
  history?: {
    location?: {
      search?: string;
    };
  };
  apiService?: unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  store: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: (action: any) => void;
  disableProgress?: boolean;
  isReview?: boolean;
}

/* ----------------------------------
   Component
---------------------------------- */

function Page({
  children,
  history = {},
  apiService = {},
  store,
  dispatch,
  disableProgress = false,
  isReview = false
}: PageProps): JSX.Element {
  const {
    customer,
    apiEvent,
    attachments,
    customer: { region = '', customerOptoutAttachments = false, lineOfCredit, recordType },
    common: { policyAccepted = false } = {}
  } = store;

  const { search = '' } = history.location || {};
  const field = 'financialCardAttachment';
  const uploadDocumentCheck = attachments?.uploads?.[field]?.files || [];

  const formPages = getFormTemplateFromCustomer(customer)?.pages || [];

  const regionWithNoAttachmentsValidation = ['latam', 'emea'];

  const hasInvalidFin = checkHasInvalidFin(regionWithNoAttachmentsValidation, region, formPages, attachments);

  const hasFinancialAttachmentStep = formPages.some(
    (pageProps: { validateFinancials?: boolean }) => pageProps.validateFinancials
  );

  const disableSubmitFunc = checkDisableSubmitFunc(disableProgress, hasInvalidFin, policyAccepted);

  const isLastPage = isReview || formPages.length === 1;
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const currentPage = window.location.pathname?.split('/').reverse()[0];

  const { t } = useTranslation();
  const shouldScrollToTopOnError = recordType !== 'Customer Set Up';

  const scrollToTop = () => {
    const options: ScrollToOptions = {
      top: 0,
      behavior: 'smooth'
    };

    window.scrollTo(options);
    document.documentElement?.scrollTo?.(options);
    document.body?.scrollTo?.(options);
    document.getElementById('root')?.scrollTo?.(options);
  };

  useEffect(() => {
    if (search && search.includes('event=signing_complete')) {
      setTimeout(() => window.scrollTo({ behavior: 'smooth', top: 9999 }), 300);
    } else {
      setTimeout(() => window.scrollTo({ behavior: 'smooth', top: 0 }), 200);
    }
  }, [search]);

  return (
    <Wrapper data-current-page={currentPage}>
      <Formik
        initialValues={customer}
        onSubmit={() =>
          handleSubmit({
            disableSubmitFunc,
            apiService: apiService as ApiServiceLikeHelper,
            customer,
            dispatch,
            history: history as { push: (path: string, state?: { submitted: boolean }) => void }
          })
        }
      >
        {({ submitForm, validateForm, errors = {} }) => (
          <Form>
            {hasSubmitted && !isEmptyObject(errors) && (
              <Notification
                titleKey={t('messages.requiredFieldsWarning')}
                subtitleKey={t('messages.requiredFieldsWarningMessage')}
              />
            )}
            {children}
            <FormButtonNavigation
              history={history}
              formPages={formPages}
              handleSubmit={async () => {
                const formErrors = await validateForm();

                if (!isEmptyObject(formErrors)) {
                  if (shouldScrollToTopOnError) {
                    setTimeout(() => scrollToTop(), 0);
                  }

                  setHasSubmitted(true);
                  dispatch(setErrorFields(formErrors));
                  return;
                }

                submitForm().then(() => {
                  dispatch(setErrorFields(errors));
                  setHasSubmitted(true);
                  window.scrollTo({ behavior: 'smooth', top: 0 });
                });
              }}
              isReview={isLastPage}
              disableProgress={
                disableProgress ||
                (lineOfCredit &&
                  isLastPage &&
                  hasFinancialAttachmentStep &&
                  !customerOptoutAttachments &&
                  uploadDocumentCheck.length === 0) ||
                (isLastPage && hasInvalidFin) ||
                apiEvent.isSubmitting ||
                (hasSubmitted && !isEmptyObject(errors))
              }
            />
          </Form>
        )}
      </Formik>
    </Wrapper>
  );
}

export default Page;
